/**
 * @file ClientSettingAssets.js
 * @description This file contains the ClientSettingAssets component, which is a reusable form for displaying and editing a client's individual asset settings.
 */

import { useState, useEffect } from 'react';
import {
    getClientOptions,
    createClientOption,
    updateClientOption,
} from '../../../services/clientService';
import Toast from '../../ui/toasts/Toast';
import {
    SecurityAttributes,
    ClientOptionEnum,
} from '../../../utils/data/enums';
import { useAuthContext } from '../../../store/Contexts';
import ClientOptionButtons from './ClientOptionButtons';
import { getText } from '../../../i18n';

const ClientSettingAssets = () => {
    const [
        initialDisplayIndividualAssetMenu,
        setInitialDisplayIndividualAssetMenu,
    ] = useState({});
    const [displayIndividualAssetMenu, setDisplayIndividualAssetMenu] =
        useState();

    const [initialAllowAssetUpload, setInitialAllowAssetUpload] = useState({});
    const [allowAssetUpload, setAllowAssetUpload] = useState();

    const [initialAllowAssetSharing, setInitialAllowAssetSharing] = useState(
        {},
    );
    const [allowAssetSharing, setAllowAssetSharing] = useState();

    const { hasAttribute } = useAuthContext();

    const getOptions = async () => {
        try {
            const data = await getClientOptions(0, [
                ClientOptionEnum.DisplayIndividualAssetMenu,
                ClientOptionEnum.AllowAssetUpload,
                ClientOptionEnum.AllowAssetSharing,
            ]);

            const displayIndividualAssetMenuOption = data.find(
                item =>
                    item.clientOptionId ===
                    ClientOptionEnum.DisplayIndividualAssetMenu,
            );
            const allowAssetUploadOption = data.find(
                item =>
                    item.clientOptionId === ClientOptionEnum.AllowAssetUpload,
            );
            const allowAssetSharingOption = data.find(
                item =>
                    item.clientOptionId === ClientOptionEnum.AllowAssetSharing,
            );

            if (
                displayIndividualAssetMenuOption &&
                allowAssetUploadOption &&
                allowAssetSharingOption
            ) {
                setInitialDisplayIndividualAssetMenu(
                    displayIndividualAssetMenuOption,
                );
                setDisplayIndividualAssetMenu(
                    displayIndividualAssetMenuOption.value,
                );

                setInitialAllowAssetUpload(allowAssetUploadOption);
                setAllowAssetUpload(allowAssetUploadOption.value);

                setInitialAllowAssetSharing(allowAssetSharingOption);
                setAllowAssetSharing(allowAssetSharingOption.value);
            } else {
                Toast.error(
                    getText('msg_ce633c7ef7e7'),
                );
            }
        } catch (error) {
            console.error('Could not fetch:', error);
        }
    };

    useEffect(() => {
        getOptions();
    }, []);

    const handleSaveOption = async (
        initialOption,
        newValue,
        setInitialOptionFunction,
    ) => {
        const option = {
            clientOptionId: initialOption.clientOptionId,
            value: `${newValue}`,
        };

        if (initialOption.usingDefaultValue) {
            const response = await createClientOption(option, 0);
            if (response.error)
                return Toast.error(
                    getText('msg_46c4bd3f2c45'),
                );
            setInitialOptionFunction(response.data);
        } else if (initialOption.value !== `${newValue}`) {
            const response = await updateClientOption(option, 0);
            if (response.error)
                return Toast.error(
                    getText('msg_f861dcfa033d'),
                );
            setInitialOptionFunction(response.data);
        }
    };

    const handleSave = async () => {
        try {
            await Promise.all([
                handleSaveOption(
                    initialDisplayIndividualAssetMenu,
                    displayIndividualAssetMenu,
                    setInitialDisplayIndividualAssetMenu,
                ),
                handleSaveOption(
                    initialAllowAssetUpload,
                    allowAssetUpload,
                    setInitialAllowAssetUpload,
                ),
                handleSaveOption(
                    initialAllowAssetSharing,
                    allowAssetSharing,
                    setInitialAllowAssetSharing,
                ),
            ]);
            Toast.success(getText('msg_808ca2a8bc13'));
        } catch (error) {
            console.error('Error saving asset client settings:', error);
        }
    };

    const handleCancel = () => {
        setDisplayIndividualAssetMenu(initialDisplayIndividualAssetMenu.value);
        setAllowAssetUpload(initialAllowAssetUpload.value);
        setAllowAssetSharing(initialAllowAssetSharing.value);
    };

    const toggleValue = current => (current === 'true' ? 'false' : 'true');

    return (
        <>
            <div
                style={{
                    marginLeft: '48px',
                    marginRight: '48px',
                    marginTop: '12px',
                }}
            >
                <div style={{ marginTop: '12px' }}>{getText('msg_c914e7eb9e62')}</div>
                <div
                    style={{
                        color: 'var(--iNGAGED-Neutral-Color2)',
                        marginBottom: '12px',
                    }}
                >
                    {getText('msg_f90e5fbb7a2b')}
                </div>

                <div style={{ marginLeft: '24px' }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className="form-switch">
                            <input
                                onClick={() =>
                                    setDisplayIndividualAssetMenu(
                                        toggleValue(displayIndividualAssetMenu),
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={displayIndividualAssetMenu === 'true'}
                            />
                        </div>
                        <div>{getText('msg_2f546eef69a8')}</div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className="form-switch">
                            <input
                                onClick={() =>
                                    setAllowAssetUpload(
                                        toggleValue(allowAssetUpload),
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={allowAssetUpload === 'true'}
                            />
                        </div>
                        <div>{getText('msg_f74c5afa0ea6')}</div>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '5px',
                            alignItems: 'anchor-center',
                            marginTop: '12px',
                        }}
                    >
                        <div className="form-switch">
                            <input
                                onClick={() =>
                                    setAllowAssetSharing(
                                        toggleValue(allowAssetSharing),
                                    )
                                }
                                className="form-check-input custom-switch-input"
                                type="checkbox"
                                checked={allowAssetSharing === 'true'}
                            />
                        </div>
                        <div>{getText('msg_1e7c0e953e41')}</div>
                    </div>
                </div>
            </div>
            <div>
                {hasAttribute(SecurityAttributes.ClientSettingsFullAccess) && (
                    <ClientOptionButtons
                        handleSave={handleSave}
                        handleCancel={handleCancel}
                    />
                )}
            </div>
        </>
    );
};

export default ClientSettingAssets;
